/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useState} from 'react';
import {
  selectGlobalVariableListState,
  setIsModelUsageDialogOpen,
  setVariableModelUsageData
} from '../../../../redux/ducks/ScenarioAnalysis/slice';
import {useAppDispatch, useAppSelector} from '../../../../redux/hooks';
import {AppDispatch} from '../../../../redux/store';
import {StyledArgosDialog} from '../../../components/Dialogs/ArgosDialog';
import {SCENARIO_ANALYSIS_CONSTANTS} from '../constants';
import ModelUsageDialog from './ModelUsageDialog';
import {IconButton, Tooltip} from '@mui/material';
import {usePostRequest} from "../../../../api/helper";
import {MRT_ColumnDef} from 'material-react-table';
import {tableButtonGray, tableHeaderGray} from '../../../../constants/colors';
import toast from 'react-hot-toast';
import {ArgosViewDetailsIcon} from '../../../components/Icons/ArgosActionIcons';
import {ArgosTableSimple} from "../../../components/Table/ArgosTableSimple";


export interface ModelUsageComponent {
  variableName: string
  displayName: string
}


interface VariablesTableRow {
  variable_name:string,
  display_name:string,
  units:string,
  i_o:string,
  data_type:string,
  min:string,
  max: string,
  enum_data: any, 
  description: string, 
  default: any
}


const gotoModelsComponent = (data: any, dispatch: AppDispatch) => {
  dispatch(setIsModelUsageDialogOpen(true))
  dispatch(setVariableModelUsageData(data))
}


/**
 * Renders the Variables Table content on the View Variables page
 */
const VariablesTable = () => {
  const dispatch = useAppDispatch()
  const isModelUsageDialogOpen = useAppSelector(state => selectGlobalVariableListState(state).isModelUsageDialogOpen)

  const [variablesTableData, setVariablesTableData] = useState<VariablesTableRow[]>([])
  const [isFetchVariablesTableLoading, setIsFetchVariablesTableLoading] = useState<boolean>(true)

  /**
   * Fetch variables table content
   */
  const variablesTableUrl = SCENARIO_ANALYSIS_CONSTANTS.viewVariables.endpoints.variablesTable
  const fetchVariablesTableHandle = usePostRequest(variablesTableUrl)
  const fetchVariablesTable = async () => {
    setIsFetchVariablesTableLoading(true)
    fetchVariablesTableHandle().then((data) => {
      setVariablesTableData(data.table_data)
    }).catch((e) => {
      console.error(e)
      toast.error("An error occurred while fetching the variables table content")
    }).then(() => {
      setIsFetchVariablesTableLoading(false)
    })
  }
  
  /**
   * Fetch and set variables table content on component mount
   */
  useEffect(() => {
    fetchVariablesTable()
  }, [])

  /**
   * Row actions for Variables Table
   */
  const tableRowActions = (row: any) => {
    const data: ModelUsageComponent = {
      variableName: row?.original?.variable_name,
      displayName: row?.original?.display_name,
    }

    return (
      <Tooltip
        title={"View Model Usage"}
        arrow
        placement="bottom"
      >
        <IconButton onClick={() => gotoModelsComponent(data, dispatch)} size="large">
          <ArgosViewDetailsIcon fontSize="medium" style={{color: tableButtonGray}} />
        </IconButton>
      </Tooltip>
    )
  }

  /**
   * Columns for Variables Table
   */
  const columns: MRT_ColumnDef<VariablesTableRow>[] = [
    {
      id: 'display_name',
      header: "Variable",
      accessorKey: "display_name",
      enableEditing: false,
      minSize: 80,
      size: 250,
    },
    {
      id: 'variable_name',
      header: "Source Name",
      accessorKey: 'variable_name',
      minSize: 80,
      size: 250,
      enableEditing: false,
    },
    {
      id: 'units',
      header: "Units",
      accessorKey: "units",
      enableEditing: false,
      minSize: 80,
      size: 120,
    },
    {
      id: 'i_o',
      header: "I/O",
      accessorKey: "i_o",
      enableEditing: false,
      minSize: 80,
      size: 110,
    },
    {
      header: 'Data Type',
      accessorFn: (row: any) => row.enum_data?.length ? "enumerated" : row.data_type ?? '',
      id: "data_type",
      enableEditing: false,
      minSize: 80,
      size: 150,
    },
    {
      header: "Min",
      accessorKey: "min",
      enableEditing: false,
      minSize: 80,
      size: 110,
    },
    {
      header: "Max",
      accessorKey: "max",
      enableEditing: false,
      minSize: 80,
      size: 110,
    },
    {
      header: "Enumerated Values",
      accessorKey: "enum_data",
      enableEditing: false,
      minSize: 80,
      size: 220,
      Cell: ({ cell }) => {
        const row = cell?.row?.original;
        const values_arr = row?.enum_data?.map((obj: any) => obj.alias)
        if (row.enum_data?.length) {
          return (
            <div>
              {values_arr.map((element: any, idx: number) => {
                return (
                  <div key={idx}>{element}</div>
                )
              })}
            </div>
          )
        } else {
          return (<div></div>)
        }
      }
    },
    {
      header: 'Default',
      accessorKey: "default",
      enableEditing: false,
      minSize: 80,
      size: 140,
      Cell: ({ cell }) => {
        const row = cell.row?.original
        return (
          <div>
            {row.enum_data?.length ?
              row?.enum_data?.filter((obj: any) => obj.value === row.default)[0].alias
            : row.default
            }
          </div>
        )
      },
    },
    {
      header: "Description",
      accessorKey: "description",
      enableEditing: true,
      minSize: 80,
      size: 250,
    }
  ]
  
  return (
    <div style={{width: '100%', height: '100%'}}>
      <ArgosTableSimple 
        columns={columns}
        data={variablesTableData}

        enableTopToolbar={true}
        enableBottomToolbar={false}
        enableRowActions
        positionActionsColumn="last"
        displayColumnDefOptions={{'mrt-row-actions': {size: 150, minSize: 50}}}
        renderRowActions={({row}: any) => tableRowActions(row)}
        enableEditing={false}
        state={{isLoading: isFetchVariablesTableLoading}}
      />
      <StyledArgosDialog
        height="100%"
        open={isModelUsageDialogOpen}
        onClose={() => {
          dispatch(setIsModelUsageDialogOpen(false))
        }}
      >
        <ModelUsageDialog />
      </StyledArgosDialog>
    </div>
  )
}

export default VariablesTable
